import React from 'react';
import {View, Text, StyleSheet, ActivityIndicator} from 'react-native';
import {StatsData} from '../../services/diretta';
import {Colors} from '../../theme/colors';
import {useTheme} from '../../hooks/useTheme';

interface StatsTabProps {
  stats: StatsData | null;
  homeTeam: string;
  awayTeam: string;
  loading?: boolean;
}

interface StatBarProps {
  label: string;
  homeValue: string;
  awayValue: string;
  colors: any;
}

const StatBar: React.FC<StatBarProps> = ({label, homeValue, awayValue, colors}) => {
  // Parse numeric values for bar width calculation
  const parseValue = (val: string): number => {
    const cleaned = String(val).replace(/[%()]/g, '').split(' ')[0];
    return parseFloat(cleaned) || 0;
  };

  const homeNum = parseValue(homeValue);
  const awayNum = parseValue(awayValue);
  const total = homeNum + awayNum;
  const homePercent = total > 0 ? (homeNum / total) * 100 : 50;
  const awayPercent = total > 0 ? (awayNum / total) * 100 : 50;

  return (
    <View style={[styles.statRow, {borderBottomColor: colors.border}]}>
      <Text style={[styles.statValueLeft, {color: colors.textPrimary}]} numberOfLines={2} adjustsFontSizeToFit>
        {homeValue}
      </Text>
      <View style={styles.centerColumn}>
        <Text style={[styles.statLabel, {color: colors.textSecondary}]}>{label}</Text>
        <View style={styles.barsContainer}>
          <View style={styles.barLeft}>
            <View style={[styles.barFillLeft, {width: `${homePercent}%`}]} />
          </View>
          <View style={styles.barRight}>
            <View style={[styles.barFillRight, {width: `${awayPercent}%`}]} />
          </View>
        </View>
      </View>
      <Text style={[styles.statValueRight, {color: colors.textPrimary}]} numberOfLines={2} adjustsFontSizeToFit>
        {awayValue}
      </Text>
    </View>
  );
};

// Group order as per website
const GROUP_ORDER = ['Statistiche Top', 'Tiri', 'Attacco', 'Passaggi', 'Difesa', 'Portiere'];

export const StatsTab: React.FC<StatsTabProps> = ({stats, loading}) => {
  const {colors} = useTheme();
  
  // Show loading spinner when loading or stats is null (initial load)
  if (loading || !stats) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
        <Text style={[styles.loadingText, {color: colors.textSecondary}]}>Caricamento statistiche...</Text>
      </View>
    );
  }

  // Check if any data exists
  const hasData = GROUP_ORDER.some(group => stats[group]?.length > 0);
  
  if (!hasData) {
    return (
      <View style={styles.emptyContainer}>
        <Text style={styles.emptyTitle}>Statistiche non disponibili</Text>
        <Text style={styles.emptyText}>
          Le statistiche saranno disponibili durante la partita.
        </Text>
      </View>
    );
  }

  return (
    <View style={[styles.container, {backgroundColor: colors.background}]}>
      {GROUP_ORDER.map((groupName) => {
        const groupStats = stats[groupName];
        if (!groupStats || groupStats.length === 0) return null;

        return (
          <View key={groupName} style={styles.groupContainer}>
            {/* Group Header */}
            <View style={styles.groupHeader}>
              <Text style={styles.groupHeaderText}>{groupName.toUpperCase()}</Text>
            </View>

            {/* Stats */}
            {groupStats.map((stat, index) => (
              <StatBar
                key={index}
                label={stat.incident_name}
                homeValue={stat.value_home}
                awayValue={stat.value_away}
                colors={colors}
              />
            ))}
          </View>
        );
      })}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#fff',
  },
  groupContainer: {
    marginBottom: 16,
  },
  groupHeader: {
    backgroundColor: Colors.primary + 'dd',
    paddingVertical: 10,
    alignItems: 'center',
  },
  groupHeaderText: {
    color: '#fff',
    fontSize: 11,
    fontWeight: '600',
    letterSpacing: 1,
  },
  statRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0, 0, 0, 0.06)',
  },
  statValueLeft: {
    width: 70,
    fontSize: 12,
    fontWeight: '500',
    color: '#333',
    textAlign: 'left',
  },
  statValueRight: {
    width: 70,
    fontSize: 12,
    fontWeight: '500',
    color: '#333',
    textAlign: 'right',
  },
  centerColumn: {
    flex: 1,
    paddingHorizontal: 10,
  },
  statLabel: {
    fontSize: 11,
    color: '#333',
    textAlign: 'center',
    marginBottom: 5,
  },
  barsContainer: {
    flexDirection: 'row',
    gap: 3,
    height: 7,
  },
  barLeft: {
    flex: 1,
    backgroundColor: 'rgba(200, 200, 210, 0.4)',
    borderRadius: 2,
    overflow: 'hidden',
    justifyContent: 'flex-end',
    flexDirection: 'row',
  },
  barRight: {
    flex: 1,
    backgroundColor: 'rgba(200, 200, 210, 0.4)',
    borderRadius: 2,
    overflow: 'hidden',
    justifyContent: 'flex-start',
    flexDirection: 'row',
  },
  barFillLeft: {
    height: '100%',
    backgroundColor: Colors.primary,
    borderRadius: 2,
  },
  barFillRight: {
    height: '100%',
    backgroundColor: 'rgba(180, 180, 190, 0.6)',
    borderRadius: 2,
  },
  loadingContainer: {
    padding: 50,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
    color: '#666',
  },
  emptyContainer: {
    padding: 30,
    alignItems: 'center',
    backgroundColor: '#f9f9f9',
    margin: 16,
    borderRadius: 12,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
  },
  emptyText: {
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
  },
});
